import { Callback } from "game-common/models"
import { Circle, Graphics, LINE_CAP } from "pixi.js"

import { isMobile } from "../../../../client_util"

// Shared circular close button for dialogs/modals. It replaces the bare "x" glyph the Dialog used to
// drop in the top-right corner: a dark disc with a light rim and a stroked X, drawn CENTERED ON ITS
// OWN ORIGIN so a caller can pin it straight onto a panel corner and let it hang over the edge.
//
// Bigger under a thumb. The stage is a fixed 640x360 letterboxed into whatever the device gives us,
// so on a phone one stage pixel is well under one CSS pixel: an 11px disc lands at roughly 14 CSS
// px across, against the ~44px a touch target wants. It is drawn from its radius and hangs off a
// corner, so growing it costs no layout anywhere.
const DESKTOP_RADIUS = 11
const TOUCH_RADIUS = 17

export const CLOSE_BUTTON_RADIUS = isMobile() ? TOUCH_RADIUS : DESKTOP_RADIUS

const RIM = 0x8d97ad
const RIM_HOVER = 0xd9a441 // gold, matching the inventory/crafting panel accent
const FILL = 0x1b2030
const FILL_HOVER = 0x2f3648
const GLYPH = 0xe6e9f0
const GLYPH_HOVER = 0xffe6b0

export interface CloseButtonProps {
    radius?: number
    onClick?: Callback
}

export class CloseButton extends Graphics {
    private radius: number

    constructor({ radius = CLOSE_BUTTON_RADIUS, onClick }: CloseButtonProps = {}) {
        super()
        this.radius = radius

        this.draw(false)

        this.interactive = true
        this.cursor = "pointer"
        // An explicit circular hit area, a couple of px proud of the rim: the disc is small and this
        // is the one control every dialog is closed by, so it should be forgiving to hit (and to tap).
        this.hitArea = new Circle(0, 0, radius + 3)

        this.on("mouseover", () => this.draw(true))
        this.on("mouseout", () => this.draw(false))
        if (onClick) {
            this.on("click", onClick)
            this.on("tap", onClick)
        }
    }

    private draw = (hover: boolean) => {
        const r = this.radius
        this.clear()

        // Fake drop shadow -- an offset dark disc behind, rather than a filter (see the budget
        // hardware target: filters cost a render pass per instance).
        this.lineStyle(0)
        this.beginFill(0x000000, 0.35)
        this.drawCircle(0, 1.5, r)
        this.endFill()

        this.lineStyle({ width: 2, color: hover ? RIM_HOVER : RIM, alpha: 1 })
        this.beginFill(hover ? FILL_HOVER : FILL, 0.98)
        this.drawCircle(0, 0, r)
        this.endFill()

        const arm = r * 0.4
        this.lineStyle({
            width: Math.max(2, r * 0.19),
            color: hover ? GLYPH_HOVER : GLYPH,
            alpha: 1,
            cap: LINE_CAP.ROUND,
        })
        this.moveTo(-arm, -arm)
        this.lineTo(arm, arm)
        this.moveTo(arm, -arm)
        this.lineTo(-arm, arm)
        this.lineStyle(0)
    }
}
